import React from "react";
import { LocaleLabels, SUPPORTED_LOCALES } from "@packages/global";
import { useTranslation } from "react-i18next";
import { useLanguageStore } from "../stores/languageStore";

interface LanguageSwitcherProps {
  compact?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ compact = false }) => {
  const { t, i18n } = useTranslation("common");
  const { locale: storedLocale, setLocale } = useLanguageStore();
  const locale = SUPPORTED_LOCALES.find((item) => item === (i18n.resolvedLanguage ?? i18n.language)) ?? storedLocale;

  if (compact) {
    const currentIndex = SUPPORTED_LOCALES.indexOf(locale);
    const nextLocale = SUPPORTED_LOCALES[(currentIndex + 1) % SUPPORTED_LOCALES.length];
    const currentLabel = LocaleLabels[locale];
    const switchLabel = t("ui.language.switchLabel", {
      current: currentLabel.full,
      next: LocaleLabels[nextLocale].full,
    });

    return (
      <button
        type="button"
        onClick={() => setLocale(nextLocale)}
        title={switchLabel}
        aria-label={switchLabel}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
      >
        <span className="text-xs font-semibold">{currentLabel.short}</span>
      </button>
    );
  }

  return (
    <div role="group" aria-label={t("ui.language.groupLabel")} className="flex items-center gap-1 rounded-lg bg-bg-elevated p-1">
      {SUPPORTED_LOCALES.map((loc) => (
        <button
          key={loc}
          type="button"
          onClick={() => setLocale(loc)}
          title={LocaleLabels[loc].full}
          aria-pressed={locale === loc}
          className={`flex flex-1 items-center justify-center rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            locale === loc ? "bg-bg-surface text-text-primary shadow-sm" : "text-text-secondary hover:text-text-primary"
          }`}
        >
          {LocaleLabels[loc].full}
        </button>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
